import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Alert,
  AlertDescription,
  Badge
} from './components/ui';
import {
  CheckCircle2,
  AlertTriangle,
  Package,
  FileText,
  Loader2,
  RefreshCw,
  Download,
  Eye,
  ArrowLeft,
  Clock,
  Utensils
} from 'lucide-react';

const OutboundFDA = () => {
  const { id: outboundId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [outboundData, setOutboundData] = useState(null);
  const [items, setItems] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [placementOptions, setPlacementOptions] = useState([]);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [selectedFoodItems, setSelectedFoodItems] = useState([]);
  const [filteredBoxes, setFilteredBoxes] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [priorNotices, setPriorNotices] = useState([]);
  const [error, setError] = useState(null);

  // Load outbound data on component mount
  useEffect(() => {
    if (outboundId) {
      loadOutboundData();
    }
  }, [outboundId]);

  // Filter boxes when food items selection changes
  useEffect(() => {
    if (selectedFoodItems.length > 0) {
      const foodItemSkus = selectedFoodItems.map(item => item.merchant_sku);
      const boxesWithFoodItems = boxes.filter(box => {
        return box.items && box.items.some(item => foodItemSkus.includes(item.merchant_sku));
      });
      setFilteredBoxes(boxesWithFoodItems);
    } else {
      setFilteredBoxes([]);
    }
  }, [selectedFoodItems, boxes]);

  const loadOutboundData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load outbound shipment data
      const outboundResponse = await fetch(`/api/outbound/${outboundId}`);
      if (!outboundResponse.ok) {
        throw new Error(`Failed to load outbound data: ${outboundResponse.status}`);
      }
      const outboundData = await outboundResponse.json();
      setOutboundData(outboundData);

      // Load items
      const itemsResponse = await fetch(`/api/outbound/${outboundId}/items`);
      if (itemsResponse.ok) {
        const itemsData = await itemsResponse.json();
        // The API returns { items: { items: [...] } }, and each item has nested structure
        const rawItems = itemsData.items?.items || [];
        const flattenedItems = rawItems.map(shipmentItem => ({
          merchant_sku: shipmentItem.item.merchant_sku,
          title: shipmentItem.item.title,
          asin: shipmentItem.item.asin,
          fnsku: shipmentItem.item.fnsku,
          quantity: shipmentItem.quantity,
          item_id: shipmentItem.item.id,
          thumbnail_url: shipmentItem.item.images?.[0]?.thumbnail_url || null
        }));
        setItems(flattenedItems);
      }

      // Load boxes
      const boxesResponse = await fetch(`/api/outbound/${outboundId}/boxes`);
      if (boxesResponse.ok) {
        const boxesData = await boxesResponse.json();
        setBoxes(boxesData.boxes || []);
      }

      // Load placement options (if available)
      const placementResponse = await fetch(`/api/outbound/${outboundId}/placement-options`);
      if (placementResponse.ok) {
        const placementData = await placementResponse.json();
        setPlacementOptions(placementData.placement_options || []);
        
        // Auto-select confirmed placement or first one
        const confirmedPlacement = placementData.placement_options?.find(option => option.confirmed_at);
        if (confirmedPlacement) {
          setSelectedPlacement(confirmedPlacement);
        } else if (placementData.placement_options?.length > 0) {
          setSelectedPlacement(placementData.placement_options[0]);
        }
      }

      // Load existing prior notices
      loadPriorNotices();

    } catch (error) {
      console.error('Error loading outbound data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPriorNotices = async () => {
    try {
      const response = await fetch(`/api/outbound/${outboundId}/prior-notices`);
      if (response.ok) {
        const data = await response.json();
        setPriorNotices(data.prior_notices || []);
      }
    } catch (error) {
      console.error('Error loading prior notices:', error);
    }
  };

  const handleFoodItemToggle = (item) => {
    setSelectedFoodItems(prev => {
      const isSelected = prev.some(selected => selected.merchant_sku === item.merchant_sku);
      if (isSelected) {
        return prev.filter(selected => selected.merchant_sku !== item.merchant_sku);
      } else {
        return [...prev, item];
      }
    });
  };

  const handlePlacementSelect = (placement) => {
    setSelectedPlacement(placement);
  };

  const generatePriorNotices = async () => {
    if (filteredBoxes.length === 0) {
      alert('No boxes with food items selected');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const payload = {
        outbound_id: outboundId,
        boxes: filteredBoxes.map(box => ({
          box_id: box.id,
          items: box.items.filter(item => 
            selectedFoodItems.some(foodItem => foodItem.merchant_sku === item.merchant_sku)
          )
        })),
        placement_option: selectedPlacement,
        food_items: selectedFoodItems
      };

      const response = await fetch('/api/outbound/generate-prior-notices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Reload prior notices to show the new ones
        await loadPriorNotices();
        
        // Download merged PDF if available
        if (result.merged_pdf_url) {
          const link = document.createElement('a');
          link.href = result.merged_pdf_url;
          link.download = `prior-notices-outbound-${outboundId}.pdf`;
          link.click();
        }
        
        alert(`Successfully generated ${result.generated_count} prior notices`);
      } else {
        throw new Error(result.error || 'Generation failed');
      }

    } catch (error) {
      console.error('Error generating prior notices:', error);
      setError(`Failed to generate prior notices: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPDF = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <h2 className="text-xl font-semibold text-slate-900">Loading FDA Tools</h2>
          <p className="text-slate-500">Fetching outbound data and items</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto mt-12">
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <strong>Error:</strong> {error}
              <Button 
                onClick={loadOutboundData} 
                variant="outline" 
                size="sm" 
                className="ml-4"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => navigate(`/outbound/${outboundId}`)}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Outbound</span>
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">FDA Prior Notices</h1>
                  <p className="text-slate-600">Outbound ID: {outboundId}</p>
                  {outboundData && (
                    <p className="text-sm text-slate-500">
                      {outboundData.client_name} • {items.length} items • {boxes.length} boxes
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={loadOutboundData}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
                title="Refresh data"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Food Items Selection */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Utensils className="w-5 h-5 text-orange-600" />
                  <h2 className="text-lg font-semibold">Select Food Items</h2>
                </div>
                <Badge variant="outline">
                  {selectedFoodItems.length} selected
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {items.length > 0 ? (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {items.map((item, index) => (
                    <div
                      key={`${item.merchant_sku}-${index}`}
                      className={`p-2.5 border rounded-lg cursor-pointer transition-colors ${
                        selectedFoodItems.some(selected => selected.merchant_sku === item.merchant_sku)
                          ? 'border-orange-300 bg-orange-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      onClick={() => handleFoodItemToggle(item)}
                    >
                      <div className="flex items-center space-x-3">
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          {item.thumbnail_url ? (
                            <img
                              src={item.thumbnail_url}
                              alt={item.title || item.merchant_sku}
                              className="w-12 h-12 object-cover rounded border border-slate-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-12 h-12 bg-slate-100 rounded border border-slate-200 flex items-center justify-center ${
                              item.thumbnail_url ? 'hidden' : 'flex'
                            }`}
                          >
                            <Package className="w-6 h-6 text-slate-400" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate" title={item.title || item.merchant_sku}>
                            {item.title || item.merchant_sku}
                          </p>
                          <div className="flex items-center space-x-2 mt-0.5">
                            <p className="text-xs text-slate-500">SKU: {item.merchant_sku}</p>
                            {item.asin && (
                              <>
                                <span className="text-xs text-slate-300">•</span>
                                <p className="text-xs text-slate-500">ASIN: {item.asin}</p>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Quantity and Selection */}
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900">Qty: {item.quantity}</p>
                          </div>
                          {selectedFoodItems.some(selected => selected.merchant_sku === item.merchant_sku) && (
                            <CheckCircle2 className="w-5 h-5 text-orange-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No items found</p>
              )}
            </CardContent>
          </Card>

          {/* Boxes with Food Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold">Boxes with Food Items</h2>
                </div>
                <Badge variant="outline">
                  {filteredBoxes.length} boxes
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {filteredBoxes.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredBoxes.map((box) => (
                    <div
                      key={box.id}
                      className="p-3 border border-slate-200 rounded bg-white"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Box {box.id}</p>
                          <p className="text-xs text-slate-500">
                            {box.items?.filter(item => 
                              selectedFoodItems.some(foodItem => foodItem.merchant_sku === item.merchant_sku)
                            ).length} food items
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Weight: {box.weight}kg</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedFoodItems.length > 0 ? (
                <p className="text-slate-500 text-center py-8">No boxes contain the selected food items</p>
              ) : (
                <p className="text-slate-500 text-center py-8">Select food items to see relevant boxes</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Placement Options */}
        {placementOptions.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <h2 className="text-lg font-semibold flex items-center space-x-2">
                <span>Placement Options</span>
                <Badge variant="outline">{placementOptions.length} available</Badge>
              </h2>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {placementOptions.map((option, index) => (
                  <div
                    key={index}
                    className={`p-4 border rounded cursor-pointer transition-colors ${
                      selectedPlacement === option
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={() => handlePlacementSelect(option)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm">Option {index + 1}</p>
                      {option.confirmed_at && (
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          Confirmed
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {option.shipments?.length || 0} shipments
                    </p>
                    {selectedPlacement === option && (
                      <CheckCircle2 className="w-4 h-4 text-blue-600 mt-2" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generate Prior Notices */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Generate Prior Notices</h2>
              <Button
                onClick={generatePriorNotices}
                disabled={isGenerating || filteredBoxes.length === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Prior Notices ({filteredBoxes.length})
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-medium text-sm mb-2">Generation Summary:</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Selected food items: {selectedFoodItems.length}</li>
                <li>• Boxes with food items: {filteredBoxes.length}</li>
                <li>• Placement option: {selectedPlacement ? 'Selected' : 'None selected'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Prior Notices History */}
        {priorNotices.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  <h2 className="text-lg font-semibold">Prior Notice History</h2>
                </div>
                <Badge variant="outline">
                  {priorNotices.length} generated
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {priorNotices.map((notice, index) => (
                  <div
                    key={index}
                    className="p-4 border border-slate-200 rounded bg-white"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          Prior Notice #{notice.id || index + 1}
                        </p>
                        <p className="text-xs text-slate-500">
                          Generated: {new Date(notice.created_at).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {notice.box_count} boxes • {notice.item_count} items
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {notice.individual_pdfs && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Show individual PDFs dropdown or modal
                              alert('Individual PDFs feature coming soon');
                            }}
                            title="View individual box PDFs"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        {notice.merged_pdf_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadPDF(notice.merged_pdf_url, `prior-notices-${notice.id}.pdf`)}
                            title="Download merged PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Badge
                          variant={notice.status === 'completed' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {notice.status || 'completed'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OutboundFDA;